/**
 * CollapsibleProductionSection
 *
 * A pure UI/UX wrapper used on the Production Files page to make the
 * major sections (Master Files, Tracklist Validation, Artwork, Upload
 * Links, …) collapsible so orders with many discs / many files stay
 * navigable.
 *
 * IMPORTANT: this component is presentational only. It never mounts /
 * unmounts its children — it just toggles their `display` via a hidden
 * wrapper — so any upload progress, in-flight fetches, or hook state
 * inside the wrapped section survives an accidental collapse. No
 * behavioural / business-logic changes.
 *
 * Props
 *  - id             stable id for testids and the shared "expand all /
 *                   collapse all" store.
 *  - title          left-aligned section header text.
 *  - icon           optional lucide-react icon node rendered next to
 *                   the title.
 *  - status         one of "complete" | "warning" | "attention" |
 *                   "pending" | null. Controls the status pill colour
 *                   and the leading ✓ / ⚠ / • glyph.
 *  - summary        short right-aligned line (e.g. "3 discs · 24
 *                   files"). Always visible, even when collapsed.
 *  - defaultOpen    initial open state (defaults to true).
 *  - openSignal     numeric counter — incrementing forces `isOpen=true`
 *                   without disabling further user toggles.
 *  - closeSignal    numeric counter — incrementing forces `isOpen=false`.
 *  - onToggle       optional callback fired with the new open state
 *                   whenever the user clicks the header.
 *  - dataTestidPrefix override for the top-level data-testid. Defaults
 *                   to `collapsible-section-${id}`.
 */
import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card } from './ui/card';

// Read a previously-persisted open/closed state for this section from
// sessionStorage (survives tab switches / remounts within the same browser
// session, but not a full new session). Falls back to `defaultOpen` when
// there is no stored value. Purely presentational — no business logic.
const readPersistedOpen = (key, fallback) => {
    if (!key || typeof window === 'undefined') return fallback;
    try {
        const v = window.sessionStorage.getItem(key);
        if (v === '1') return true;
        if (v === '0') return false;
    } catch (_e) { /* storage unavailable — use fallback */ }
    return fallback;
};

const STATUS_STYLES = {
    complete: {
        pill: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        glyph: '✓',
    },
    warning: {
        pill: 'bg-amber-50 text-amber-700 border-amber-200',
        glyph: '⚠',
    },
    attention: {
        pill: 'bg-rose-50 text-rose-700 border-rose-200',
        glyph: '!',
    },
    pending: {
        pill: 'bg-slate-50 text-slate-600 border-slate-200',
        glyph: '•',
    },
};

export function CollapsibleProductionSection({
    id,
    title,
    icon = null,
    status = null,
    statusLabel = null,
    summary = null,
    defaultOpen = true,
    openSignal = 0,
    closeSignal = 0,
    onToggle,
    dataTestidPrefix,
    persistKey = null,
    children,
    className = '',
}) {
    const [isOpen, setIsOpen] = useState(() => readPersistedOpen(persistKey, defaultOpen));

    // Persist the open/closed state for the session so it survives tab
    // switches and remounts (requirement: "preserve expansion state during
    // the session"). Runs on every change — manual toggle, expand-all and
    // collapse-all all funnel through `setIsOpen`.
    useEffect(() => {
        if (!persistKey || typeof window === 'undefined') return;
        try {
            window.sessionStorage.setItem(persistKey, isOpen ? '1' : '0');
        } catch (_e) { /* storage unavailable — ignore */ }
    }, [persistKey, isOpen]);

    // React to the shared expand-all / collapse-all controls in the
    // parent. Each control increments its own counter so we always run
    // the effect exactly once per click, even when a user has since
    // toggled the section manually.
    useEffect(() => {
        if (openSignal > 0) setIsOpen(true);
    }, [openSignal]);
    useEffect(() => {
        if (closeSignal > 0) setIsOpen(false);
    }, [closeSignal]);

    const testidBase = dataTestidPrefix || `collapsible-section-${id}`;

    const handleToggle = () => {
        setIsOpen((prev) => {
            const next = !prev;
            if (typeof onToggle === 'function') onToggle(next);
            return next;
        });
    };

    const statusStyle = status ? STATUS_STYLES[status] : null;

    return (
        <Card id={id} className={`mb-6 ${className}`} data-testid={testidBase}>
            <button
                type="button"
                onClick={handleToggle}
                aria-expanded={isOpen}
                aria-controls={`${testidBase}-content`}
                className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-slate-50/60 transition-colors"
                data-testid={`${testidBase}-toggle`}
            >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    {statusStyle && (
                        <span
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold ${statusStyle.pill}`}
                            aria-hidden="true"
                            data-testid={`${testidBase}-status-glyph`}
                        >
                            {statusStyle.glyph}
                        </span>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            {icon}
                            <span className="text-lg font-semibold text-slate-900 truncate">
                                {title}
                            </span>
                            {statusLabel && (
                                <span
                                    className={`hidden sm:inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusStyle ? statusStyle.pill : 'bg-slate-50 text-slate-600 border-slate-200'}`}
                                    data-testid={`${testidBase}-status-label`}
                                >
                                    {statusLabel}
                                </span>
                            )}
                        </div>
                        {summary && (
                            <div
                                className="text-sm text-slate-500 mt-0.5 truncate"
                                data-testid={`${testidBase}-summary`}
                            >
                                {summary}
                            </div>
                        )}
                    </div>
                </div>
                <ChevronDown
                    className={`h-5 w-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    data-testid={`${testidBase}-chevron`}
                />
            </button>
            {/* Children stay mounted even when the section is collapsed
                so in-flight uploads, fetches, and internal state never
                get torn down. We just hide the region visually. */}
            <div
                id={`${testidBase}-content`}
                data-testid={`${testidBase}-content`}
                aria-hidden={!isOpen}
                className={isOpen ? 'block' : 'hidden'}
            >
                <div className="border-t border-slate-100">
                    {children}
                </div>
            </div>
        </Card>
    );
}

export default CollapsibleProductionSection;
